'use client';

import { useId, type ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { CategoryRuleTarget } from '@/types/category-budget';
import { remainingDescriptors, type RuleAnchorOption } from './ruleAnchor';

/**
 * The scope explanation and its evidence, behind a ⓘ popover (the home widgets'
 * WidgetInfoButton affordance). Inline it was tall enough that, with a rule armed, the
 * category list had no room left to navigate on a phone — the row keeps only the name and
 * the choice, the reach lives here. A sibling of the <label>, never inside it: a tap on
 * the info button must open the popover, not toggle the radio.
 */
function AnchorInfoButton({ option }: { option: RuleAnchorOption }): ReactNode {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={option.kind === 'merchant'
            ? `Sobre a regra para o estabelecimento ${option.label}`
            : 'Sobre a regra só para esta descrição'}
          className="-m-1.5 shrink-0 cursor-pointer rounded-full p-1.5 text-text-tertiary transition-colors hover:text-text-secondary"
        >
          <InfoIcon size={15} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" collisionPadding={12} className="w-72 rounded-xl p-4">
        {option.kind === 'merchant' ? (
          <>
            {/* Paired with the descriptor option's line so the scope difference lands on
                one word: QUALQUER lançamento versus APENAS este texto. */}
            <p className="text-xs text-text-secondary">
              Vale para qualquer lançamento de{' '}
              <b className="text-text-primary">{option.label}</b>, inclusive quando o banco
              mudar o texto.
            </p>
            {/* The reach, shown rather than asserted. Omitted when the API sent no
                spread — an empty chip row would claim a reach of nothing. */}
            {option.sampleDescriptors && option.sampleDescriptors.length > 0 && (
              <span className="mt-2 flex flex-wrap items-center gap-1.5">
                {option.sampleDescriptors.map((descriptor) => (
                  <span
                    key={descriptor}
                    className="rounded-full bg-surface-sunken px-2 py-0.5 font-mono text-3xs text-text-tertiary"
                  >
                    {descriptor}
                  </span>
                ))}
                {remainingDescriptors(option) > 0 && (
                  <span className="text-3xs text-text-tertiary">
                    +{remainingDescriptors(option)}{' '}
                    {remainingDescriptors(option) === 1 ? 'descritor' : 'descritores'}
                  </span>
                )}
              </span>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-text-secondary">
              Vale apenas para lançamentos com este mesmo texto.
            </p>
            {/* The anchor's OWN label, not a second string: the descriptor key is built
                from the merchant name when there is one, so showing the raw description
                here would name text the rule does not match. */}
            <span className="mt-2 block truncate rounded-lg bg-surface-sunken px-2 py-1 font-mono text-3xs text-text-tertiary">
              {option.label}
            </span>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Criar regra para" — the anchor the rule and its retro-apply will use. Rendered only
 * where there really are two anchors; with one the whole block disappears rather than
 * showing a disabled option, because the majority of transactions (people, keyless rows)
 * would carry a permanently dead row for the sake of teaching.
 *
 * One line per option by design: this fieldset shares a 92dvh sheet with the category
 * list and the save button, so everything beyond the name and the radio sits behind the
 * per-option ⓘ popover.
 */
export function RuleAnchorChoice({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: RuleAnchorOption[];
  value: CategoryRuleTarget;
  onChange: (kind: CategoryRuleTarget) => void;
  disabled?: boolean;
}) {
  const name = useId();
  if (options.length < 2) return null;

  return (
    <fieldset className="min-w-0">
      <legend className="text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
        Criar regra para
      </legend>
      <div className="mt-2 flex flex-col gap-1.5">
        {options.map((option) => {
          const isSelected = option.kind === value;
          return (
            <div
              key={option.kind}
              className={`flex items-center gap-2.5 rounded-xl border px-2.5 py-2 ${
                isSelected ? 'border-primary bg-brand-50/50' : 'border-border-subtle'
              } ${disabled ? 'opacity-60' : ''}`}
            >
              <label
                className={`flex min-w-0 flex-1 items-center gap-2.5 ${
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  className="accent-brand-primary"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onChange(option.kind)}
                />
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-semibold text-text-primary">
                    {option.kind === 'merchant' ? 'Este estabelecimento' : 'Só esta descrição'}
                  </span>
                  {option.kind === 'merchant' && (
                    <span className="shrink-0 rounded-full border border-border bg-surface px-1.5 py-0.5 text-3xs font-medium text-text-secondary">
                      recomendado
                    </span>
                  )}
                </span>
              </label>
              <AnchorInfoButton option={option} />
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
